import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/api";
import { isUnavailableBrandColor, themeInputColor } from "@/brand";
import { useAuth } from "@/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowUp, ArrowDown, Plus, Trash2, Save, LayoutGrid, ListPlus, GitBranch, Palette, FileText, Zap, BookmarkPlus, Target } from "lucide-react";

const TABS = [
  { key: "modules", label: "Modules", icon: LayoutGrid },
  { key: "custom_fields", label: "Custom Fields", icon: ListPlus },
  { key: "pipelines", label: "Pipelines", icon: GitBranch },
  { key: "theme", label: "Theme", icon: Palette },
  { key: "pdf_templates", label: "PDF Templates", icon: FileText },
  { key: "automations", label: "Automations", icon: Zap },
  { key: "goals", label: "Goals", icon: Target },
];
const FIELD_ENTITIES = ["leads", "quotations", "jobs", "orders", "products", "bills"];
const FIELD_TYPES = ["text", "number", "date", "dropdown", "multi-select", "checkbox", "currency"];
const TRIGGERS = [{ v: "lead_idle", l: "Lead idle for N days" }, { v: "invoice_overdue", l: "Invoice overdue by N days" }];
const ACTIONS = [{ v: "notify_owner", l: "Notify workspace owner" }, { v: "notify_customer", l: "Send customer reminder" }];

export default function Workspace() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [tab, setTab] = useState("modules");
  const [saving, setSaving] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [tplName, setTplName] = useState("");
  const [err, setErr] = useState("");
  const [tenants, setTenants] = useState([]);
  const [slug, setSlug] = useState("");

  const isSuper = user?.role === "super_admin" && !(user?.tenant_ids?.length);
  const load = () => {
    setErr("");
    api.get("/workspace/config", { params: slug ? { slug } : {}, timeout: 12000 })
      .then((r) => { setCfg(r.data.config); setTenant(r.data.tenant); })
      .catch((e) => setErr(e.response?.data?.detail || e.message || "Failed to load workspace config"));
  };
  useEffect(() => {
    const t = setTimeout(() => { if (!cfg) setErr("Workspace is taking too long to load — check your connection and retry."); }, 12000);
    load();
    return () => clearTimeout(t);
  }, [slug]);
  useEffect(() => {
    if (isSuper) api.get("/platform/tenants").then((r) => setTenants(r.data)).catch(() => {});
  }, [isSuper]);

  const save = async (section, value) => {
    setSaving(true);
    try {
      const r = await api.patch("/workspace/config", { section, value, slug: slug || undefined });
      setCfg(r.data);
      toast.success("Saved — workspace updated");
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  if (err && !cfg) return (
    <div className="max-w-md mx-auto mt-16 rounded-2xl border bg-card p-8 text-center space-y-4" data-testid="workspace-error">
      <div className="font-heading font-bold text-lg">Workspace failed to load</div>
      <p className="text-sm text-danger-foreground">{err}</p>
      <Button data-testid="workspace-retry" onClick={() => { setErr(""); load(); }} className="bg-primary hover:bg-primary-hover">Retry</Button>
    </div>
  );
  if (!cfg) return (
    <div className="space-y-4" data-testid="workspace-loading">
      <div className="h-8 w-64 rounded-lg bg-muted animate-pulse" />
      <div className="flex gap-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-9 w-28 rounded-full bg-muted animate-pulse" />)}</div>
      <div className="h-72 rounded-2xl bg-muted animate-pulse" />
    </div>
  );

  const setSection = (k, v) => setCfg({ ...cfg, [k]: v });

  return (
    <div className="space-y-5" data-testid="workspace-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Workspace Studio</h1>
          <p className="text-sm text-muted-foreground">Customize {tenant?.name} — modules, fields, pipelines, theme & automations. No code needed.</p>
        </div>
        <div className="flex gap-2 items-center">
          {isSuper && tenants.length > 0 && (
            <Select value={slug || tenant?.slug || ""} onValueChange={(v) => { setCfg(null); setSlug(v); }}>
              <SelectTrigger data-testid="workspace-tenant-select" className="w-52"><SelectValue placeholder="Pick workspace" /></SelectTrigger>
              <SelectContent>{tenants.map((t) => <SelectItem key={t.slug} value={t.slug}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button variant="outline" data-testid="save-as-template-button" onClick={() => setTplOpen(true)}>
            <BookmarkPlus size={15} className="mr-2" /> Save as Template
          </Button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="workspace-tabs">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`ws-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`text-xs px-3 py-2 rounded-full border font-medium transition-colors flex items-center gap-1.5 ${tab === t.key ? "bg-primary text-inverse border-primary" : "bg-card hover:bg-accent"}`}>
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "modules" && <ModulesTab cfg={cfg} setSection={setSection} save={save} saving={saving} />}
      {tab === "custom_fields" && <FieldsTab cfg={cfg} setSection={setSection} save={save} saving={saving} />}
      {tab === "pipelines" && <PipelinesTab cfg={cfg} setSection={setSection} save={save} saving={saving} />}
      {tab === "theme" && <ThemeTab cfg={cfg} setSection={setSection} save={save} saving={saving} />}
      {tab === "pdf_templates" && <PdfTab cfg={cfg} setSection={setSection} save={save} saving={saving} />}
      {tab === "automations" && <AutomationsTab cfg={cfg} setSection={setSection} save={save} saving={saving} />}
      {tab === "goals" && (
        <Card className="p-5 space-y-4" data-testid="goals-tab">
          <p className="text-sm text-muted-foreground">Monthly targets power the goal rings on the Analytics Studio dashboard.</p>
          <div className="grid sm:grid-cols-2 gap-4 max-w-xl">
            <div>
              <label className="text-sm font-medium">Monthly revenue target (₹)</label>
              <Input type="number" data-testid="goal-revenue-input" className="mt-1.5" value={cfg.goals?.monthly_revenue_target ?? 0}
                onChange={(e) => setSection("goals", { ...cfg.goals, monthly_revenue_target: parseFloat(e.target.value) || 0 })} />
            </div>
            <div>
              <label className="text-sm font-medium">Monthly leads target</label>
              <Input type="number" data-testid="goal-leads-input" className="mt-1.5" value={cfg.goals?.monthly_leads_target ?? 0}
                onChange={(e) => setSection("goals", { ...cfg.goals, monthly_leads_target: parseInt(e.target.value) || 0 })} />
            </div>
          </div>
          <SaveBar testid="goals-save-button" saving={saving} onSave={() => save("goals", cfg.goals)} />
        </Card>
      )}

      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent data-testid="save-template-dialog">
          <DialogHeader><DialogTitle className="font-heading">Save current setup as template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Other workspaces can start from this exact configuration (modules, fields, pipelines, theme) — data is never copied.</p>
            <Input data-testid="template-name-input" placeholder="Template name, e.g. My Cleaning Setup" value={tplName} onChange={(e) => setTplName(e.target.value)} />
            <Button data-testid="template-save-button" className="w-full bg-primary hover:bg-primary-hover" onClick={async () => {
              try { await api.post("/workspace/save-as-template", { name: tplName }); toast.success("Template saved"); setTplOpen(false); setTplName(""); }
              catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
            }}>Save Template</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SaveBar({ onSave, saving, testid }) {
  return (
    <div className="flex justify-end">
      <Button data-testid={testid} onClick={onSave} disabled={saving} className="bg-primary hover:bg-primary-hover">
        <Save size={15} className="mr-2" /> {saving ? "Saving…" : "Save Changes"}
      </Button>
    </div>
  );
}

function ModulesTab({ cfg, setSection, save, saving }) {
  const modules = [...cfg.modules].sort((a, b) => a.order - b.order);
  const move = (key, dir) => {
    const arr = [...modules];
    const i = arr.findIndex((m) => m.key === key);
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setSection("modules", arr.map((m, idx) => ({ ...m, order: idx })));
  };
  return (
    <Card className="p-5 space-y-3" data-testid="modules-tab">
      <p className="text-sm text-muted-foreground">Toggle modules on/off, rename menu labels, and reorder the sidebar. Changes apply to everyone in this workspace.</p>
      {modules.map((m) => (
        <div key={m.key} data-testid={`module-row-${m.key}`} className="flex items-center gap-3 border rounded-lg px-3 py-2.5">
          <button data-testid={`module-toggle-${m.key}`} onClick={() => setSection("modules", cfg.modules.map((x) => x.key === m.key ? { ...x, enabled: !x.enabled } : x))}
            className={`w-10 h-6 rounded-full transition-colors relative ${m.enabled ? "bg-primary" : "bg-primary-tint dark:bg-surface-elevated"}`}>
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-surface shadow transition-all ${m.enabled ? "left-4.5 left-[18px]" : "left-0.5"}`} />
          </button>
          <Input value={m.label} data-testid={`module-label-${m.key}`} className="h-8 max-w-48"
            onChange={(e) => setSection("modules", cfg.modules.map((x) => x.key === m.key ? { ...x, label: e.target.value } : x))} />
          <span className="text-xs text-muted-foreground font-mono">{m.key}</span>
          <div className="flex-1" />
          <button data-testid={`module-up-${m.key}`} onClick={() => move(m.key, -1)} className="p-1.5 rounded hover:bg-accent"><ArrowUp size={14} /></button>
          <button data-testid={`module-down-${m.key}`} onClick={() => move(m.key, 1)} className="p-1.5 rounded hover:bg-accent"><ArrowDown size={14} /></button>
        </div>
      ))}
      <SaveBar testid="modules-save-button" saving={saving} onSave={() => save("modules", cfg.modules)} />
    </Card>
  );
}

function FieldsTab({ cfg, setSection, save, saving }) {
  const [entity, setEntity] = useState("leads");
  const [draft, setDraft] = useState({ label: "", type: "text", options: "" });
  const fields = cfg.custom_fields?.[entity] || [];
  const updateEntity = (list) => setSection("custom_fields", { ...cfg.custom_fields, [entity]: list });
  const addField = () => {
    if (!draft.label.trim()) return toast.error("Field label required");
    const key = draft.label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
    if (fields.some((f) => f.key === key)) return toast.error("Field already exists");
    updateEntity([...fields, { key, label: draft.label, type: draft.type, options: draft.options ? draft.options.split(",").map((s) => s.trim()).filter(Boolean) : [] }]);
    setDraft({ label: "", type: "text", options: "" });
  };
  return (
    <Card className="p-5 space-y-4" data-testid="fields-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground flex-1">Fields appear in forms, detail views and PDFs. Stored as flexible data — no schema change needed.</p>
        <Select value={entity} onValueChange={setEntity}>
          <SelectTrigger data-testid="field-entity-select" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>{FIELD_ENTITIES.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {fields.map((f) => (
        <div key={f.key} data-testid={`field-row-${f.key}`} className="flex items-center gap-3 border rounded-lg px-3 py-2.5 text-sm">
          <b>{f.label}</b>
          <span className="text-xs px-2 py-0.5 rounded-full bg-primary text-primary border border-primary">{f.type}</span>
          {f.options?.length > 0 && <span className="text-xs text-muted-foreground">{f.options.join(", ")}</span>}
          <div className="flex-1" />
          <button data-testid={`field-delete-${f.key}`} onClick={() => updateEntity(fields.filter((x) => x.key !== f.key))} className="p-1.5 rounded hover:bg-danger text-danger-foreground"><Trash2 size={14} /></button>
        </div>
      ))}
      {!fields.length && <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-6 text-center">No custom fields for {entity} yet — add your first below.</div>}
      <div className="grid sm:grid-cols-4 gap-2 border-t pt-4">
        <Input data-testid="field-label-input" placeholder="Field label" value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} />
        <Select value={draft.type} onValueChange={(v) => setDraft({ ...draft, type: v })}>
          <SelectTrigger data-testid="field-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>{FIELD_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        <Input data-testid="field-options-input" placeholder="Options (comma separated)" value={draft.options} onChange={(e) => setDraft({ ...draft, options: e.target.value })}
          disabled={!["dropdown", "multi-select"].includes(draft.type)} />
        <Button data-testid="field-add-button" variant="outline" onClick={addField}><Plus size={14} className="mr-1" /> Add Field</Button>
      </div>
      <SaveBar testid="fields-save-button" saving={saving} onSave={() => save("custom_fields", cfg.custom_fields)} />
    </Card>
  );
}

function PipelinesTab({ cfg, setSection, save, saving }) {
  const entities = Object.keys(cfg.pipelines || {});
  const [entity, setEntity] = useState(entities[0] || "leads");
  const [label, setLabel] = useState("");
  const stages = cfg.pipelines?.[entity] || [];
  const update = (list) => setSection("pipelines", { ...cfg.pipelines, [entity]: list });
  return (
    <Card className="p-5 space-y-4" data-testid="pipelines-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground flex-1">Edit stage names and colors per pipeline.</p>
        <Select value={entity} onValueChange={setEntity}>
          <SelectTrigger data-testid="pipeline-entity-select" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>{entities.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {stages.map((s, i) => (
        <div key={s.key} data-testid={`stage-row-${s.key}`} className="flex items-center gap-3 border rounded-lg px-3 py-2.5">
          <input type="color" value={s.color} data-testid={`stage-color-${s.key}`} className="w-8 h-8 rounded cursor-pointer border"
            onChange={(e) => update(stages.map((x) => x.key === s.key ? { ...x, color: e.target.value } : x))} />
          <Input value={s.key} className="h-8 max-w-48" data-testid={`stage-label-${s.key}`}
            onChange={(e) => update(stages.map((x, xi) => xi === i ? { ...x, key: e.target.value } : x))} />
          <div className="flex-1" />
          <button data-testid={`stage-delete-${s.key}`} onClick={() => update(stages.filter((_, xi) => xi !== i))} className="p-1.5 rounded hover:bg-danger text-danger-foreground"><Trash2 size={14} /></button>
        </div>
      ))}
      <div className="flex gap-2 border-t pt-4">
        <Input data-testid="stage-add-input" placeholder="New stage name" value={label} onChange={(e) => setLabel(e.target.value)} className="max-w-56" />
        <Button data-testid="stage-add-button" variant="outline" onClick={() => { if (label.trim()) { update([...stages, { key: label.trim(), color: "hsl(var(--primary))" }]); setLabel(""); } }}><Plus size={14} className="mr-1" /> Add Stage</Button>
      </div>
      <SaveBar testid="pipelines-save-button" saving={saving} onSave={() => save("pipelines", cfg.pipelines)} />
    </Card>
  );
}

function ThemeTab({ cfg, setSection, save, saving }) {
  const t = cfg.theme || {};
  const [colorUnavailable, setColorUnavailable] = useState(false);
  const set = (k, v) => setSection("theme", { ...t, [k]: v });
  const setPrimaryColor = (value) => {
    if (isUnavailableBrandColor(value)) setColorUnavailable(true);
    else { setColorUnavailable(false); set("primary_color", value); }
  };
  return (
    <Card className="p-5 space-y-4" data-testid="theme-tab">
      <p className="text-sm text-muted-foreground">Brand theme for this workspace — applied to the sidebar accent, PDFs and login feel.</p>
      <div className="grid sm:grid-cols-2 gap-4 max-w-xl">
        <div>
          <label className="text-sm font-medium">Primary color</label>
          <div className="flex gap-2 mt-1.5">
            <input type="color" data-testid="theme-primary-color" value={t.primary_color || themeInputColor("--primary-input-hex")} onChange={(e) => setPrimaryColor(e.target.value)} className="w-11 h-11 rounded-lg border cursor-pointer" />
            <Input value={t.primary_color || ""} onChange={(e) => setPrimaryColor(e.target.value)} className="font-mono" />
          </div>
          {colorUnavailable && <p role="alert" className="mt-1 text-xs text-danger-foreground">{"\u0047reen is not available in this theme"}</p>}
        </div>
        <div>
          <label className="text-sm font-medium">Logo URL</label>
          <Input data-testid="theme-logo-input" className="mt-1.5" placeholder="https://…/logo.png" value={t.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} />
        </div>
        <div>
          <label className="text-sm font-medium">Default mode</label>
          <Select value={t.dark_default ? "dark" : "light"} onValueChange={(v) => set("dark_default", v === "dark")}>
            <SelectTrigger data-testid="theme-mode-select" className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="light">Light</SelectItem><SelectItem value="dark">Dark</SelectItem></SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex items-center gap-3 border rounded-xl p-4 max-w-xl" style={{ borderColor: t.primary_color }}>
        <div className="w-10 h-10 rounded-xl" style={{ background: t.primary_color }} />
        <div>
          <div className="font-heading font-bold" style={{ color: t.primary_color }}>{t.primary_color} preview</div>
          <div className="text-xs text-muted-foreground">This is how your brand accent will feel.</div>
        </div>
      </div>
      <SaveBar testid="theme-save-button" saving={saving} onSave={() => {
        if (isUnavailableBrandColor(t.primary_color)) setColorUnavailable(true);
        else save("theme", t);
      }} />
    </Card>
  );
}

function PdfTab({ cfg, setSection, save, saving }) {
  const [doc, setDoc] = useState("quotation");
  const tpl = cfg.pdf_templates?.[doc] || {};
  const set = (k, v) => setSection("pdf_templates", { ...cfg.pdf_templates, [doc]: { ...tpl, [k]: v } });
  return (
    <Card className="p-5 space-y-4" data-testid="pdf-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground flex-1">Header, footer, terms and accent color baked into generated {doc} PDFs.</p>
        <Select value={doc} onValueChange={setDoc}>
          <SelectTrigger data-testid="pdf-doctype-select" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="quotation">Quotation</SelectItem><SelectItem value="invoice">Invoice</SelectItem></SelectContent>
        </Select>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 max-w-3xl">
        <div><label className="text-sm font-medium">Header line</label><Input data-testid="pdf-header-input" className="mt-1.5" value={tpl.header || ""} onChange={(e) => set("header", e.target.value)} placeholder="e.g. ISO 9001 Certified" /></div>
        <div>
          <label className="text-sm font-medium">Accent color</label>
          <div className="flex gap-2 mt-1.5">
            <input type="color" data-testid="pdf-accent-color" value={tpl.accent_color || themeInputColor("--primary-input-hex")} onChange={(e) => set("accent_color", e.target.value)} className="w-10 h-10 rounded-lg border cursor-pointer" />
            <Input value={tpl.accent_color || ""} onChange={(e) => set("accent_color", e.target.value)} className="font-mono" />
          </div>
        </div>
        <div><label className="text-sm font-medium">Footer line</label><Input data-testid="pdf-footer-input" className="mt-1.5" value={tpl.footer || ""} onChange={(e) => set("footer", e.target.value)} placeholder="e.g. Thank you for your business" /></div>
        <div><label className="text-sm font-medium">Terms & Conditions</label><Textarea data-testid="pdf-terms-input" className="mt-1.5" rows={3} value={tpl.terms || ""} onChange={(e) => set("terms", e.target.value)} placeholder="Payment due within 7 days…" /></div>
      </div>
      <SaveBar testid="pdf-save-button" saving={saving} onSave={() => save("pdf_templates", cfg.pdf_templates)} />
    </Card>
  );
}

function AutomationsTab({ cfg, setSection, save, saving }) {
  const rules = cfg.automations || [];
  const update = (list) => setSection("automations", list);
  return (
    <Card className="p-5 space-y-4" data-testid="automations-tab">
      <p className="text-sm text-muted-foreground">Simple if-this-then-that rules, checked automatically every 15 minutes.</p>
      {rules.map((r, i) => (
        <div key={r.id} data-testid={`rule-row-${r.id}`} className="flex items-center gap-3 border rounded-lg px-3 py-2.5 flex-wrap">
          <button data-testid={`rule-toggle-${r.id}`} onClick={() => update(rules.map((x) => x.id === r.id ? { ...x, enabled: !x.enabled } : x))}
            className={`w-10 h-6 rounded-full transition-colors relative ${r.enabled ? "bg-primary" : "bg-primary-tint dark:bg-surface-elevated"}`}>
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-surface shadow transition-all ${r.enabled ? "left-[18px]" : "left-0.5"}`} />
          </button>
          <span className="text-sm">When <b>{TRIGGERS.find((t) => t.v === r.trigger)?.l || r.trigger}</b></span>
          <Input type="number" min="1" className="w-20 h-8" data-testid={`rule-days-${r.id}`} value={r.days}
            onChange={(e) => update(rules.map((x) => x.id === r.id ? { ...x, days: parseInt(e.target.value) || 1 } : x))} />
          <span className="text-sm">days → <b>{ACTIONS.find((a) => a.v === r.action)?.l || r.action}</b></span>
          <div className="flex-1" />
          <button data-testid={`rule-delete-${r.id}`} onClick={() => update(rules.filter((_, xi) => xi !== i))} className="p-1.5 rounded hover:bg-danger text-danger-foreground"><Trash2 size={14} /></button>
        </div>
      ))}
      <Button data-testid="rule-add-button" variant="outline" onClick={() => update([...rules, { id: `r${Date.now()}`, trigger: "lead_idle", days: 2, action: "notify_owner", enabled: true }])}>
        <Plus size={14} className="mr-1" /> Add Rule
      </Button>
      <SaveBar testid="automations-save-button" saving={saving} onSave={() => save("automations", rules)} />
    </Card>
  );
}
